import React, { useMemo, useRef, useState } from 'react';
import { Form, Input, Select, Tabs } from 'antd';
import { isDefaultLocale, useActiveLocales } from '../../hooks/useActiveLocales';

type TranslationMap = Record<string, Record<string, unknown>>;

function mergePreservedLocales(
  cleaned: TranslationMap,
  formTranslations: TranslationMap | undefined,
  existing?: unknown,
): TranslationMap {
  const submittedLocales = new Set(Object.keys(formTranslations || {}));
  const merged: TranslationMap = { ...cleaned };

  if (existing && typeof existing === 'object') {
    for (const [locale, data] of Object.entries(existing as Record<string, unknown>)) {
      if (isDefaultLocale(locale)) continue;
      if (submittedLocales.has(locale)) continue;
      if (data && typeof data === 'object') {
        merged[locale] = data as Record<string, unknown>;
      }
    }
  }

  return merged;
}

/** Strip empty locales and normalize proTips to string[]. Merge with existing. */
export function normalizeEquipmentTranslations(
  translations?: TranslationMap,
  existing?: unknown,
): TranslationMap | null {
  const cleaned: TranslationMap = {};

  if (translations && typeof translations === 'object') {
    for (const [locale, data] of Object.entries(translations)) {
      if (!data || typeof data !== 'object') continue;
      if (isDefaultLocale(locale)) continue;

      const name = typeof data.name === 'string' ? data.name.trim() : '';
      const description = typeof data.description === 'string' ? data.description.trim() : '';

      let proTips: string[] = [];
      if (Array.isArray(data.proTips)) {
        proTips = data.proTips.map(String).map((s) => s.trim()).filter(Boolean);
      } else if (typeof data.proTips === 'string' && data.proTips.trim()) {
        proTips = data.proTips.split('\n').map((s) => s.trim()).filter(Boolean);
      }

      if (!name && !description && proTips.length === 0) continue;

      cleaned[locale] = {
        ...(name ? { name } : {}),
        ...(description ? { description } : {}),
        ...(proTips.length > 0 ? { proTips } : {}),
      };
    }
  }

  const merged = mergePreservedLocales(cleaned, translations, existing);
  return Object.keys(merged).length > 0 ? merged : null;
}

function tabLabel(label: string, hasError: boolean): React.ReactNode {
  if (!hasError) return label;
  return (
    <span>
      {label}
      <span style={{ color: '#ff4d4f', marginLeft: 4 }}>•</span>
    </span>
  );
}

/**
 * Language tabs for equipment catalog localized fields.
 * Default locale (en) writes top-level name/description/proTips;
 * other locales write into translations[locale].
 */
export const EquipmentLanguageTabs: React.FC = () => {
  const { locales } = useActiveLocales();
  const form = Form.useFormInstance();
  const [activeKey, setActiveKey] = useState('en');
  const lastErrorSig = useRef('');

  const otherLocales = useMemo(
    () => locales.filter((l) => !isDefaultLocale(l.code)),
    [locales],
  );
  const defaultLabel =
    locales.find((l) => isDefaultLocale(l.code))?.label || 'English';

  return (
    <Form.Item noStyle shouldUpdate>
      {() => {
        const errorFields = form
          .getFieldsError()
          .filter((f) => f.errors.length > 0);
        const errorSig = errorFields.map((e) => e.name.join('.')).join('|');

        if (errorFields.length > 0 && errorSig !== lastErrorSig.current) {
          lastErrorSig.current = errorSig;
          const first = errorFields[0]?.name;
          let target: string | null = null;
          if (Array.isArray(first)) {
            if (first[0] === 'name' || first[0] === 'description' || first[0] === 'proTips') {
              target = 'en';
            } else if (first[0] === 'translations' && first[1]) {
              target = String(first[1]);
            }
          }
          if (target && target !== activeKey) {
            queueMicrotask(() => setActiveKey(target!));
          }
        } else if (errorFields.length === 0) {
          lastErrorSig.current = '';
        }

        const enHasError = errorFields.some(
          (e) =>
            Array.isArray(e.name) &&
            (e.name[0] === 'name' || e.name[0] === 'description' || e.name[0] === 'proTips'),
        );

        const items = [
          {
            key: 'en',
            label: tabLabel(defaultLabel, enHasError),
            forceRender: true,
            children: (
              <div style={{ paddingTop: 8 }}>
                <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                  <Input placeholder="e.g. Leg Press Machine" />
                </Form.Item>
                <Form.Item label="Description" name="description" rules={[{ required: true }]}>
                  <Input.TextArea rows={4} placeholder="Describe the equipment..." />
                </Form.Item>
                <Form.Item label="Pro Tips" name="proTips">
                  <Select mode="tags" placeholder="e.g. Keep your core engaged" />
                </Form.Item>
              </div>
            ),
          },
          ...otherLocales.map((locale) => {
            const localeHasError = errorFields.some(
              (e) =>
                Array.isArray(e.name) &&
                e.name[0] === 'translations' &&
                e.name[1] === locale.code,
            );
            return {
              key: locale.code,
              label: tabLabel(locale.label, localeHasError),
              forceRender: true,
              children: (
                <div style={{ paddingTop: 8 }}>
                  <Form.Item label="Name" name={['translations', locale.code, 'name']}>
                    <Input placeholder="Translated name" />
                  </Form.Item>
                  <Form.Item label="Description" name={['translations', locale.code, 'description']}>
                    <Input.TextArea rows={4} placeholder="Translated description" />
                  </Form.Item>
                  <Form.Item label="Pro Tips" name={['translations', locale.code, 'proTips']}>
                    <Select mode="tags" placeholder="One tip per entry" />
                  </Form.Item>
                </div>
              ),
            };
          }),
        ];

        return (
          <Tabs
            activeKey={activeKey}
            onChange={setActiveKey}
            items={items}
            style={{ marginBottom: 8 }}
          />
        );
      }}
    </Form.Item>
  );
};

export { isDefaultLocale } from '../../hooks/useActiveLocales';
